"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import styles from "./ModalDialog.module.css";

const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
].join(",");

// Runs synchronously after commit in the browser; useEffect on the server so
// React does not warn about layout effects during SSR.
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface ModalDialogProps {
    open: boolean;
    mobileBottomSheet?: boolean;
    onClose: () => void;
    /** id of the element that names the dialog. Preferred over `ariaLabel`. */
    labelledBy?: string | undefined;
    /** id of the element that describes the dialog. */
    describedBy?: string | undefined;
    /** Accessible name when there is no visible heading to point at. */
    ariaLabel?: string | undefined;
    /** Element to focus on open. Defaults to the first focusable control. */
    initialFocusRef?: RefObject<HTMLElement | null> | undefined;
    /** Clicking the backdrop closes the dialog. Defaults to true. */
    closeOnBackdrop?: boolean | undefined;
    /** Classes for the panel. Supply width, background, and padding here. */
    panelClassName?: string | undefined;
    /** Classes for the fixed overlay, e.g. to raise its z-index above a sticky header. */
    overlayClassName?: string | undefined;
    children: ReactNode;
}

/**
 * Accessible modal shell shared by the site-wide promo pop-up and the
 * financing contact gate.
 *
 * What it guarantees: `role="dialog"` + `aria-modal`, an accessible name,
 * Tab/Shift+Tab kept inside the panel, focus moved in on open and restored on
 * close, Escape and backdrop-click to dismiss, body scroll locked while open,
 * and enter/exit motion that respects `prefers-reduced-motion`.
 *
 * Layout is mobile-first: the panel is full width with a small gutter, capped
 * to the dynamic viewport height, and scrolls internally rather than pushing
 * the close control off screen.
 */
export default function ModalDialog({
    open,
    mobileBottomSheet = false,
    onClose,
    labelledBy,
    describedBy,
    ariaLabel,
    initialFocusRef,
    closeOnBackdrop = true,
    panelClassName = "",
    overlayClassName = "",
    children
}: ModalDialogProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const restoreFocusRef = useRef<HTMLElement | null>(null);
    const [mounted, setMounted] = useState(false);
    const [entered, setEntered] = useState(false);

    // Portals need a DOM; wait for the client.
    useEffect(() => setMounted(true), []);

    // Mobile keyboards resize/pan the visual viewport independently of 100dvh.
    useEffect(() => {
        if (!open || !mounted || !mobileBottomSheet) return;
        const viewport = window.visualViewport;
        let frame = 0;
        const update = () => {
            overlayRef.current?.style.setProperty("--sheet-viewport-height", `${viewport?.height ?? window.innerHeight}px`);
            overlayRef.current?.style.setProperty("--sheet-viewport-top", `${viewport?.offsetTop ?? 0}px`);
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const active = document.activeElement;
                if (window.innerWidth < 640 && active instanceof HTMLElement && panelRef.current?.contains(active)) {
                    active.scrollIntoView({ block: "nearest" });
                }
            });
        };
        update();
        viewport?.addEventListener("resize", update);
        viewport?.addEventListener("scroll", update);
        window.addEventListener("resize", update);
        return () => {
            cancelAnimationFrame(frame);
            viewport?.removeEventListener("resize", update);
            viewport?.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [open, mounted, mobileBottomSheet]);

    // Drive the enter transition one frame after mount so the initial styles
    // are painted first.
    useEffect(() => {
        if (!open) {
            setEntered(false);
            return;
        }
        const frame = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(frame);
    }, [open]);

    // Lock page scroll behind the dialog and restore whatever was there before.
    useEffect(() => {
        if (!open) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    // Move focus into the dialog on open, put it back on close.
    useIsomorphicLayoutEffect(() => {
        if (!open || !mounted) return;
        restoreFocusRef.current = document.activeElement as HTMLElement | null;

        const target =
            initialFocusRef?.current ??
            panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
            panelRef.current;
        target?.focus({ preventScroll: true });

        return () => {
            const previous = restoreFocusRef.current;
            if (previous && typeof previous.focus === "function" && document.contains(previous)) {
                previous.focus({ preventScroll: true });
            }
        };
    }, [open, mounted, initialFocusRef]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== "Tab" || !panelRef.current) return;

            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
                (el) => el.offsetParent !== null || el === document.activeElement
            );
            if (focusable.length === 0) {
                event.preventDefault();
                panelRef.current.focus();
                return;
            }
            const first = focusable[0]!;
            const last = focusable[focusable.length - 1]!;
            const active = document.activeElement;

            if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || !panelRef.current.contains(active))) {
                event.preventDefault();
                first.focus();
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (!open) return;
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, handleKeyDown]);

    if (!open || !mounted) return null;

    const motion = "t-transition t-duration-200 t-ease-out motion-reduce:t-transition-none";

    return createPortal(
        <div
            ref={overlayRef}
            className={`t-fixed t-inset-0 t-z-[1000] t-flex t-justify-center ${mobileBottomSheet ? styles["sheetOverlay"] : "t-items-center t-overflow-y-auto t-p-4 sm:t-p-6"} ${overlayClassName}`.trim()}
            style={mobileBottomSheet ? undefined : {
                paddingTop: "max(1rem, env(safe-area-inset-top))",
                paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
            }}>
            <div
                className={`t-fixed t-inset-0 t-bg-brand-ink/60 t-backdrop-blur-[2px] ${motion} ${entered ? "t-opacity-100" : "t-opacity-0"}`}
                onClick={closeOnBackdrop ? onClose : undefined}
                aria-hidden="true"
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                {...(labelledBy ? { "aria-labelledby": labelledBy } : {})}
                {...(describedBy ? { "aria-describedby": describedBy } : {})}
                {...(!labelledBy && ariaLabel ? { "aria-label": ariaLabel } : {})}
                tabIndex={-1}
                data-entered={entered}
                className={`t-relative ${mobileBottomSheet ? styles["sheetPanel"] : "t-max-h-[calc(100dvh-2rem)]"} t-w-full t-overflow-y-auto t-outline-none ${motion} ${
                    entered ? "t-translate-y-0 t-scale-100 t-opacity-100" : "t-translate-y-3 t-scale-[0.98] t-opacity-0"
                } ${panelClassName}`.trim()}>
                {children}
            </div>
        </div>,
        document.body
    );
}
